import React, { useEffect, useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import { FiEdit2, FiMessageSquare, FiPlus, FiRefreshCw, FiSave, FiTrash2 } from 'react-icons/fi';
import ApiURL from '../Api/Api';

const emptyFaq = {
  id: 0,
  question: '',
  answer: '',
  keywords: '',
  display_order: 0,
  status: 'active',
};

const assetUrl = (path) => {
  if (!path) return '';
  if (/^https?:\/\//i.test(path)) return path;
  return `${ApiURL.replace(/\/$/, '')}/${String(path).replace(/^\/+/, '')}`;
};

const SuperAdminChatHelp = () => {
  const [settings, setSettings] = useState({
    title: 'Help Center',
    greeting: 'Hi! How can I help you?',
    placeholder: 'Need Help? Ask us anything',
    fallback_answer: 'I could not match that exactly. Please talk to us on WhatsApp for personal help.',
    whatsapp_number: '',
    whatsapp_label: 'Talk on WhatsApp',
    avatar_path: '',
    status: 'active',
  });
  const [avatarPreview, setAvatarPreview] = useState('');
  const [faqs, setFaqs] = useState([]);
  const [reports, setReports] = useState([]);
  const [incidentTypes, setIncidentTypes] = useState([]);
  const [faqForm, setFaqForm] = useState(emptyFaq);
  const [incidentTypeForm, setIncidentTypeForm] = useState({ id: 0, title: '', display_order: 0, status: 'active' });
  const [isLoading, setIsLoading] = useState(true);
  const [isSavingSettings, setIsSavingSettings] = useState(false);
  const [isSavingFaq, setIsSavingFaq] = useState(false);

  const fallbackReports = useMemo(
    () => reports.filter((report) => report.match_type === 'fallback' || report.match_type === 'incident'),
    [reports]
  );

  const loadChatHelp = async () => {
    setIsLoading(true);
    try {
      const response = await fetch(`${ApiURL}/superadmin_get_chatbox.php`, { credentials: 'include' });
      const data = await response.json();
      if (data.status !== 'success') {
        throw new Error(data.message || 'Failed to load chat help');
      }
      setSettings((prev) => ({ ...prev, ...(data.settings || {}) }));
      setAvatarPreview(assetUrl(data.settings?.avatar_path));
      setFaqs(data.faqs || []);
      setReports(data.reports || []);
      setIncidentTypes(data.incident_types || []);
    } catch (error) {
      toast.error(error.message || 'Failed to load chat help');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadChatHelp();
  }, []);

  const handleAvatar = (file) => {
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      toast.error('Please choose an image');
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      const result = String(reader.result || '');
      setAvatarPreview(result);
      setSettings((prev) => ({ ...prev, avatar_image: result }));
    };
    reader.readAsDataURL(file);
  };

  const saveSettings = async () => {
    setIsSavingSettings(true);
    try {
      const response = await fetch(`${ApiURL}/superadmin_save_chatbox_settings.php`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(settings),
      });
      const data = await response.json();
      if (data.status !== 'success') {
        throw new Error(data.message || 'Failed to save settings');
      }
      toast.success('Chatbox settings saved');
      loadChatHelp();
    } catch (error) {
      toast.error(error.message || 'Failed to save settings');
    } finally {
      setIsSavingSettings(false);
    }
  };

  const saveFaq = async () => {
    if (!faqForm.question.trim() || !faqForm.answer.trim()) {
      toast.error('Question and answer are required');
      return;
    }

    setIsSavingFaq(true);
    try {
      const response = await fetch(`${ApiURL}/superadmin_save_chatbox_faq.php`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(faqForm),
      });
      const data = await response.json();
      if (data.status !== 'success') {
        throw new Error(data.message || 'Failed to save FAQ');
      }
      toast.success(faqForm.id ? 'FAQ updated' : 'FAQ added');
      setFaqForm(emptyFaq);
      loadChatHelp();
    } catch (error) {
      toast.error(error.message || 'Failed to save FAQ');
    } finally {
      setIsSavingFaq(false);
    }
  };

  const deleteFaq = async (faq) => {
    if (!window.confirm(`Delete "${faq.question}"?`)) return;
    try {
      const response = await fetch(`${ApiURL}/superadmin_delete_chatbox_faq.php`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: faq.id }),
      });
      const data = await response.json();
      if (data.status !== 'success') {
        throw new Error(data.message || 'Failed to delete FAQ');
      }
      toast.success('FAQ deleted');
      loadChatHelp();
    } catch (error) {
      toast.error(error.message || 'Failed to delete FAQ');
    }
  };

  const saveIncidentType = async () => {
    if (!incidentTypeForm.title.trim()) {
      toast.error('Incident type title is required');
      return;
    }
    try {
      const response = await fetch(`${ApiURL}/superadmin_save_incident_type.php`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(incidentTypeForm),
      });
      const data = await response.json();
      if (data.status !== 'success') {
        throw new Error(data.message || 'Failed to save incident type');
      }
      toast.success('Incident type saved');
      setIncidentTypeForm({ id: 0, title: '', display_order: 0, status: 'active' });
      loadChatHelp();
    } catch (error) {
      toast.error(error.message || 'Failed to save incident type');
    }
  };

  if (isLoading) {
    return <div className="py-20 text-center text-sm text-slate-500">Loading chat help...</div>;
  }

  return (
    <div className="mt-8 space-y-6">
      <div className="grid gap-6 xl:grid-cols-[1.05fr_0.95fr]">
        <section className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
          <div className="flex items-center justify-between gap-4">
            <div>
              <h3 className="text-xl font-semibold text-slate-950">Chatbox Settings</h3>
              <p className="mt-1 text-sm text-slate-500">Controls the company owner help widget.</p>
            </div>
            <button
              type="button"
              onClick={loadChatHelp}
              className="inline-flex items-center gap-2 rounded-xl border border-slate-200 px-3 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50"
            >
              <FiRefreshCw /> Refresh
            </button>
          </div>

          <div className="mt-5 grid gap-4">
            <input
              value={settings.title || ''}
              onChange={(event) => setSettings((prev) => ({ ...prev, title: event.target.value }))}
              className="rounded-xl border border-slate-200 px-4 py-3 text-sm outline-none focus:border-cyan-500 focus:ring-2 focus:ring-cyan-100"
              placeholder="Help Center"
            />
            <textarea
              value={settings.greeting || ''}
              onChange={(event) => setSettings((prev) => ({ ...prev, greeting: event.target.value }))}
              className="min-h-[96px] rounded-xl border border-slate-200 px-4 py-3 text-sm outline-none focus:border-cyan-500 focus:ring-2 focus:ring-cyan-100"
              placeholder="Hi! How can I help you?"
            />
            <input
              value={settings.placeholder || ''}
              onChange={(event) => setSettings((prev) => ({ ...prev, placeholder: event.target.value }))}
              className="rounded-xl border border-slate-200 px-4 py-3 text-sm outline-none focus:border-cyan-500 focus:ring-2 focus:ring-cyan-100"
              placeholder="Need Help? Ask us anything"
            />
            <textarea
              value={settings.fallback_answer || ''}
              onChange={(event) => setSettings((prev) => ({ ...prev, fallback_answer: event.target.value }))}
              className="min-h-[112px] rounded-xl border border-slate-200 px-4 py-3 text-sm outline-none focus:border-cyan-500 focus:ring-2 focus:ring-cyan-100"
              placeholder="Fallback answer"
            />
            <div className="grid gap-4 sm:grid-cols-2">
              <input
                value={settings.whatsapp_number || ''}
                onChange={(event) => setSettings((prev) => ({ ...prev, whatsapp_number: event.target.value }))}
                className="rounded-xl border border-slate-200 px-4 py-3 text-sm outline-none focus:border-cyan-500 focus:ring-2 focus:ring-cyan-100"
                placeholder="919999999999"
              />
              <input
                value={settings.whatsapp_label || ''}
                onChange={(event) => setSettings((prev) => ({ ...prev, whatsapp_label: event.target.value }))}
                className="rounded-xl border border-slate-200 px-4 py-3 text-sm outline-none focus:border-cyan-500 focus:ring-2 focus:ring-cyan-100"
                placeholder="Talk on WhatsApp"
              />
            </div>
            <div className="grid gap-4 sm:grid-cols-[1fr_1fr]">
              <select
                value={settings.status || 'active'}
                onChange={(event) => setSettings((prev) => ({ ...prev, status: event.target.value }))}
                className="rounded-xl border border-slate-200 px-4 py-3 text-sm outline-none focus:border-cyan-500 focus:ring-2 focus:ring-cyan-100"
              >
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
              </select>
              <label className="flex cursor-pointer items-center justify-between gap-3 rounded-xl border border-slate-200 px-4 py-3 text-sm font-semibold text-slate-700 hover:bg-slate-50">
                <span>Choose Avatar</span>
                <input type="file" accept="image/*" onChange={(event) => handleAvatar(event.target.files?.[0])} className="hidden" />
              </label>
            </div>
            {avatarPreview && (
              <img src={avatarPreview} alt="" className="h-16 w-16 rounded-full border border-slate-200 object-cover" />
            )}
            <button
              type="button"
              onClick={saveSettings}
              disabled={isSavingSettings}
              className="inline-flex w-fit items-center gap-2 rounded-xl bg-cyan-500 px-5 py-3 text-sm font-semibold text-slate-950 shadow-sm transition hover:bg-cyan-400 disabled:cursor-not-allowed disabled:bg-slate-300"
            >
              <FiSave /> {isSavingSettings ? 'Saving...' : 'Save Chat Settings'}
            </button>
          </div>
        </section>

        <section className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
          <h3 className="text-xl font-semibold text-slate-950">Current FAQs</h3>
          <p className="mt-1 text-sm text-slate-500">First three active FAQs become owner quick options.</p>
          <div className="mt-5 max-h-[520px] space-y-3 overflow-y-auto pr-1">
            {faqs.length === 0 && <div className="rounded-xl bg-slate-50 p-4 text-sm text-slate-500">No FAQs added yet.</div>}
            {faqs.map((faq) => (
              <div key={faq.id} className="rounded-xl border border-slate-200 p-4">
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <div className="text-xs font-bold uppercase tracking-[0.18em] text-cyan-700">{faq.status}</div>
                    <h4 className="mt-1 text-sm font-semibold text-slate-950">{faq.question}</h4>
                  </div>
                  <div className="flex gap-2">
                    <button
                      type="button"
                      onClick={() => setFaqForm({ ...faq, display_order: Number(faq.display_order || 0) })}
                      className="rounded-lg border border-slate-200 p-2 text-slate-600 hover:bg-slate-50"
                      aria-label="Edit FAQ"
                    >
                      <FiEdit2 />
                    </button>
                    <button
                      type="button"
                      onClick={() => deleteFaq(faq)}
                      className="rounded-lg border border-red-100 p-2 text-red-600 hover:bg-red-50"
                      aria-label="Delete FAQ"
                    >
                      <FiTrash2 />
                    </button>
                  </div>
                </div>
                <p className="mt-2 text-sm leading-6 text-slate-600">{faq.answer}</p>
                {faq.keywords && <p className="mt-2 text-xs text-slate-400">Keywords: {faq.keywords}</p>}
              </div>
            ))}
          </div>
        </section>
      </div>

      <section className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
        <div className="flex items-center gap-2">
          <FiMessageSquare className="text-cyan-600" />
          <h3 className="text-xl font-semibold text-slate-950">Chat Questions & Answers</h3>
        </div>
        <div className="mt-5 grid gap-4 lg:grid-cols-[1fr_1fr]">
          <input
            value={faqForm.question}
            onChange={(event) => setFaqForm((prev) => ({ ...prev, question: event.target.value }))}
            className="rounded-xl border border-slate-200 px-4 py-3 text-sm outline-none focus:border-cyan-500 focus:ring-2 focus:ring-cyan-100 lg:col-span-2"
            placeholder="Question chip text"
          />
          <textarea
            value={faqForm.answer}
            onChange={(event) => setFaqForm((prev) => ({ ...prev, answer: event.target.value }))}
            className="min-h-[128px] rounded-xl border border-slate-200 px-4 py-3 text-sm outline-none focus:border-cyan-500 focus:ring-2 focus:ring-cyan-100 lg:col-span-2"
            placeholder="Answer text"
          />
          <input
            value={faqForm.keywords || ''}
            onChange={(event) => setFaqForm((prev) => ({ ...prev, keywords: event.target.value }))}
            className="rounded-xl border border-slate-200 px-4 py-3 text-sm outline-none focus:border-cyan-500 focus:ring-2 focus:ring-cyan-100 lg:col-span-2"
            placeholder="Keywords separated by comma"
          />
          <input
            type="number"
            value={faqForm.display_order}
            onChange={(event) => setFaqForm((prev) => ({ ...prev, display_order: Number(event.target.value || 0) }))}
            className="rounded-xl border border-slate-200 px-4 py-3 text-sm outline-none focus:border-cyan-500 focus:ring-2 focus:ring-cyan-100"
            placeholder="Order"
          />
          <select
            value={faqForm.status}
            onChange={(event) => setFaqForm((prev) => ({ ...prev, status: event.target.value }))}
            className="rounded-xl border border-slate-200 px-4 py-3 text-sm outline-none focus:border-cyan-500 focus:ring-2 focus:ring-cyan-100"
          >
            <option value="active">Active</option>
            <option value="inactive">Inactive</option>
          </select>
        </div>
        <div className="mt-5 flex flex-wrap gap-3">
          <button
            type="button"
            onClick={saveFaq}
            disabled={isSavingFaq}
            className="inline-flex items-center gap-2 rounded-xl bg-slate-950 px-5 py-3 text-sm font-semibold text-white transition hover:bg-slate-800 disabled:cursor-not-allowed disabled:bg-slate-300"
          >
            <FiPlus /> {isSavingFaq ? 'Saving...' : faqForm.id ? 'Update FAQ' : 'Add FAQ'}
          </button>
          {faqForm.id > 0 && (
            <button
              type="button"
              onClick={() => setFaqForm(emptyFaq)}
              className="rounded-xl border border-slate-200 px-5 py-3 text-sm font-semibold text-slate-700 hover:bg-slate-50"
            >
              Cancel Edit
            </button>
          )}
        </div>
      </section>

      <section className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
        <h3 className="text-xl font-semibold text-slate-950">Incident Options</h3>
        <p className="mt-1 text-sm text-slate-500">These options appear when a company owner raises an incident from chat.</p>
        <div className="mt-5 grid gap-3 lg:grid-cols-[1fr_140px_160px_auto]">
          <input
            value={incidentTypeForm.title}
            onChange={(event) => setIncidentTypeForm((prev) => ({ ...prev, title: event.target.value }))}
            className="rounded-xl border border-slate-200 px-4 py-3 text-sm outline-none focus:border-cyan-500 focus:ring-2 focus:ring-cyan-100"
            placeholder="Incident option"
          />
          <input
            type="number"
            value={incidentTypeForm.display_order}
            onChange={(event) => setIncidentTypeForm((prev) => ({ ...prev, display_order: Number(event.target.value || 0) }))}
            className="rounded-xl border border-slate-200 px-4 py-3 text-sm outline-none focus:border-cyan-500 focus:ring-2 focus:ring-cyan-100"
            placeholder="Order"
          />
          <select
            value={incidentTypeForm.status}
            onChange={(event) => setIncidentTypeForm((prev) => ({ ...prev, status: event.target.value }))}
            className="rounded-xl border border-slate-200 px-4 py-3 text-sm outline-none focus:border-cyan-500 focus:ring-2 focus:ring-cyan-100"
          >
            <option value="active">Active</option>
            <option value="inactive">Inactive</option>
          </select>
          <button
            type="button"
            onClick={saveIncidentType}
            className="rounded-xl bg-slate-950 px-5 py-3 text-sm font-semibold text-white hover:bg-slate-800"
          >
            {incidentTypeForm.id ? 'Update' : 'Add'}
          </button>
        </div>
        <div className="mt-4 flex flex-wrap gap-2">
          {incidentTypes.map((type) => (
            <button
              key={type.id}
              type="button"
              onClick={() => setIncidentTypeForm(type)}
              className={`rounded-full px-3 py-1.5 text-xs font-bold ${type.status === 'active' ? 'bg-cyan-100 text-cyan-900' : 'bg-slate-100 text-slate-500'}`}
            >
              {type.title}
            </button>
          ))}
        </div>
      </section>

      <section className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
        <div className="flex flex-col gap-2 sm:flex-row sm:items-end sm:justify-between">
          <div>
            <h3 className="text-xl font-semibold text-slate-950">Recent Chat Reports</h3>
            <p className="mt-1 text-sm text-slate-500">
              {fallbackReports.length} fallback/incident message{fallbackReports.length === 1 ? '' : 's'} need review.
            </p>
          </div>
        </div>
        <div className="mt-5 max-h-[540px] space-y-3 overflow-y-auto pr-1">
          {reports.length === 0 && <div className="rounded-xl bg-slate-50 p-4 text-sm text-slate-500">No chat reports yet.</div>}
          {reports.map((report) => (
            <div key={report.id} className="rounded-xl border border-slate-200 p-4">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <span className={`rounded-full px-3 py-1 text-xs font-bold uppercase tracking-[0.16em] ${
                  report.match_type === 'incident' ? 'bg-rose-100 text-rose-800' : report.match_type === 'fallback' ? 'bg-amber-100 text-amber-800' : 'bg-emerald-100 text-emerald-800'
                }`}>
                  {report.match_type}
                </span>
                <span className="text-xs text-slate-400">{report.created_at}</span>
              </div>
              <div className="mt-3 text-sm font-semibold text-slate-950">{report.message}</div>
              <p className="mt-2 text-sm leading-6 text-slate-600">{report.response}</p>
              <div className="mt-3 text-xs text-slate-400">
                {report.company_name || `Company #${report.company_id}`} | {report.owner_name || `Owner #${report.company_owner_id}`} | Score {Number(report.score || 0).toFixed(0)}
              </div>
              {(report.match_type === 'fallback' || report.match_type === 'incident') && (
                <button
                  type="button"
                  onClick={() => setFaqForm({ ...emptyFaq, question: report.message, keywords: report.message })}
                  className="mt-3 rounded-lg border border-cyan-100 px-3 py-2 text-xs font-semibold text-cyan-800 hover:bg-cyan-50"
                >
                  Train This Question
                </button>
              )}
            </div>
          ))}
        </div>
      </section>
    </div>
  );
};

export default SuperAdminChatHelp;
